import { EventEmitter, Injectable, Output } from '@angular/core'
import { environment } from '../../../environments/environment'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Router } from '@angular/router'
import { Token, User } from '../models/auth.model'
import { map } from 'rxjs/internal/operators/map'
import { throwError } from 'rxjs/internal/observable/throwError'
import { catchError } from 'rxjs/internal/operators/catchError'
import { jwtDecode } from "jwt-decode"
import { SnackbarService } from './snackbar.service'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { CustomValidators } from '../../pages/auth/custom-validator'
import { passwordValidator } from '../../shared/directives/password.validator'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authToken = environment.API_SECRET_KEY
  private readonly baseURL = environment.AUTH_URL+'/api/auth/'

  constructor(
    private http: HttpClient, 
    private router: Router,
    private snackbarService: SnackbarService
  ) { }

  registerForm = new FormGroup({
    user: new FormControl('', [Validators.required]),
    name: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, passwordValidator]),
    passwordConfirm: new FormControl('', [Validators.required])
  },
    { validators: CustomValidators.passwordsMatching }
  )

  loginForm = new FormGroup({
    id: new FormControl(null, [Validators.required]),
    password: new FormControl(null, [Validators.required]),
  })

  passwordForm = new FormGroup({
    password: new FormControl('', [Validators.required, passwordValidator]),
    passwordConfirm: new FormControl('', [Validators.required])
  },
    { validators: CustomValidators.passwordsMatching }
  )

  newsletterForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    acceptTerms: new FormControl(true, [Validators.requiredTrue])
  })
  
  @Output() userLoggedEvent = new EventEmitter<null>()

  UserLogged(){
    this.userLoggedEvent.emit()
  }

  inscribirse(){
    return this.http.post(this.baseURL+'inscribirse', this.newsletterForm.value)
    .pipe(catchError(this.errorHandler.bind(this)))
  }
  fetchUserPreferences(){
    return this.http.get(this.baseURL+'preferences')
    .pipe(catchError(this.errorHandler.bind(this)))
  }

  passwordChange(passwords: any){
    return this.http.post(this.baseURL+'password-change', passwords)
    .pipe(catchError(this.errorHandler.bind(this)))
  }

  getUserDetails(){
    const token = this.getToken()
    if(!token) return null

    try {
      const decodedToken: any = jwtDecode(token)
      return decodedToken
    } catch (err) {
      return null
    }
  }
  
  private saveToken(token: Token) {
    if (typeof window === 'undefined') return
    localStorage.setItem('access_token', token.access_token)
  }

  getAuthToken() {
    return this.authToken ?? ''
  }

  getToken() {
    if (typeof window === 'undefined') return ''
    return localStorage.getItem('access_token') ?? ''
  }

  setAuthToken(access: string) {
    if (typeof window === 'undefined') return
    localStorage.setItem('access_token', access)
  }

  refreshToken() {
    return this.http.post(this.baseURL+'refresh', {}, { withCredentials: true })
    .pipe(map((resp) => (resp as any).access_token), catchError(this.errorHandler.bind(this)))
  }

  public logout() {
    this.http.post(this.baseURL+'logout', {}, { withCredentials: true })
    .pipe(catchError(this.errorHandler.bind(this)))
    .subscribe(() => {
      if (typeof window != 'undefined' || typeof document != 'undefined') localStorage.removeItem('access_token')
      this.UserLogged()
      if (this.router.url !== '/') this.router.navigateByUrl('/')
    })
  }

  public isLoggedIn(): boolean {
    const user = this.getUserDetails()
    return user ? true : false
  }

  public isEditor(): boolean {
    const user = this.getUserDetails()
    if(user) return user.role_id > 1
    else return false
  }

  public isMaintenance(): boolean {
    const user = this.getUserDetails()
    if(user) return user.role_id >= 0
    else return false
  }

  public isAdmin(): boolean {
    const user = this.getUserDetails()
    if(user) return user.role_id > 2
    else return false
  }

  public getUsername(): string {
    const user = this.getUserDetails()
    if(user) return user.username
    else return ''
  }

  public isRoot(): boolean {
    const user = this.getUserDetails()
    if(user) return user.role_id > 3
    else return false
  }

  public register(user: User) {
    return this.http.post(this.baseURL+'register', user)
    .pipe(
      map((data) => {
        const token = data as Token
        if (token) this.saveToken(token)
        return token
      }),catchError(this.errorHandler.bind(this))
    )
  }
  
  public login(user: {id:string, password:string}) {
    return this.http.post(this.baseURL+'login', user, { withCredentials: true })
    .pipe(
      map((data) => {
        const token = data as Token
        if(token.requirePasswordChange){
          this.router.navigate(['/auth/passwdchange'])
        }
        if (token){
          this.saveToken(token)
          this.UserLogged()
        }
        return token
      }),catchError(this.errorHandler.bind(this))
    )
  }
  
  private triedAutoLogin = false
  public autoLogin() {
    if (this.triedAutoLogin) return
    this.triedAutoLogin = true

    return this.http.post(this.baseURL+'refresh', {}, { withCredentials: true })
    .subscribe({
      next: (data: any) => {
        if (data?.access_token) {
          this.setAuthToken(data.access_token)
          this.UserLogged()
        }
      },
      error: () => {
        this.UserLogged()
      }
    })
  }
  
  private errorHandler(error: HttpErrorResponse) {
    if (error.status === 0) console.error(new Date() + ': An error occurred:', error.error)
    else this.snackbarService.showSnackbar(error.error)
    return throwError(() => new Error())
  }
}
